// Accounts and groups share ONE ordering space inside a
// (entityId, type, parentGroupId) scope. This is the single total order both
// the editor draft and the server placement read, so a gesture and the row it
// persists cannot disagree on where a tie lands.

export type ScopeMemberKind = "account" | "group";

export interface ScopeOrderMember {
    id: string;
    kind: ScopeMemberKind;
    name: string;
    sortOrder: number;
}

// Where a new row lands within its scope:
// - `first`: the front of the scope (a group / type header's first child).
// - `afterAccount`: immediately after a sibling account (an account host).
// - `append`: the end of the scope (the default footer behaviour).
export type ScopeInsertPosition =
    | { kind: "first" }
    | { kind: "afterAccount"; afterAccountId: string }
    | { kind: "append" };

// Everything after `sortOrder`. Sentinel-tied and projection-tied rows are
// common, so leaving locale or input order as an implicit last key would
// persist a different order than the one the gesture showed.
export const compareScopeOrderTiebreak = (
    left: Pick<ScopeOrderMember, "id" | "kind" | "name">,
    right: Pick<ScopeOrderMember, "id" | "kind" | "name">,
): number => {
    if (left.kind !== right.kind) {
        return left.kind === "account" ? -1 : 1;
    }
    const byName = left.name.localeCompare(right.name, "en");
    if (byName !== 0) {
        return byName;
    }
    if (left.id === right.id) {
        return 0;
    }
    return left.id < right.id ? -1 : 1;
};

export const compareScopeOrder = (
    left: ScopeOrderMember,
    right: ScopeOrderMember,
): number => {
    if (left.sortOrder !== right.sortOrder) {
        return left.sortOrder - right.sortOrder;
    }
    return compareScopeOrderTiebreak(left, right);
};

// An `afterAccount` anchor that is not in `orderedIds` is the caller's
// problem: the draft appends, the server 4xxs.
interface ResolveInsertIndexParams {
    orderedIds: string[];
    position: ScopeInsertPosition;
}

export const resolveInsertIndex = ({
    orderedIds,
    position,
}: ResolveInsertIndexParams): number => {
    if (position.kind === "first") {
        return 0;
    }
    if (position.kind === "afterAccount") {
        const referenceIndex = orderedIds.indexOf(position.afterAccountId);
        return referenceIndex >= 0 ? referenceIndex + 1 : orderedIds.length;
    }
    return orderedIds.length;
};
